/**
 * Admin Wave AW7 — ローカル写真の Storage 並列アップロード + クライアントサムネ生成 (docs/108 High3)。
 *
 * old2 `apps/admin/src/lib/import/importExecutor.ts` の uploadLocalPhotos を踏襲:
 *   - 原寸を `districts/{districtId}/posts/{postId}/original.jpg` に
 *   - サムネを `districts/{districtId}/posts/{postId}/thumbnail.jpg` に
 *   - 並列度 5 で chunk アップロード
 *   - downloadURL も取得して呼出側で photoUrl / photoThumbUrl として importExecutor に渡す
 *
 * v2 差分:
 *   - old2 は `browser-image-compression` で WebWorker サムネ生成していたが、v2 admin に当該 dep が
 *     **無い** (docs/108 「browser-image-compression があれば使用、無ければ原寸のみ」)。新規 dep 追加は
 *     scope 外のため、**canvas ベースの内製サムネ生成** (外部依存なし、`generateThumbnail`) を用いる。
 *     canvas が使えない環境 (test 等) では原寸を thumbnail として流用する degrade。
 *   - 結果は postId をキーにせず rowIndex キーで返し、呼出側で MappedRow.internal に
 *     photoPath/photoThumbPath/photoUrl/photoThumbUrl を注入してから CSV を再構築する。
 */

import { getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import { storage } from '../../lib/firebase';
import type { MappedRow } from './columnMapping';

export interface PhotoUploadInfo {
  photoPath: string;
  photoUrl: string | null;
  photoThumbPath: string;
  photoThumbUrl: string | null;
}

export interface PhotoUploadProgress {
  current: number;
  total: number;
  currentFile?: string;
  /** 完了済ファイル名 (失敗は「(失敗)」付き)。 */
  completedFiles: string[];
}

const CONCURRENCY = 5;
const THUMB_MAX_EDGE = 400;
const THUMB_QUALITY = 0.8;

/**
 * canvas で長辺 THUMB_MAX_EDGE px に縮小した JPEG Blob を生成 (外部依存なし)。
 * 失敗時 (canvas / createImageBitmap 非対応、decode 失敗) は null を返し、呼出側で原寸 fallback。
 */
export async function generateThumbnail(file: File): Promise<Blob | null> {
  if (typeof document === 'undefined' || typeof createImageBitmap !== 'function') return null;
  try {
    const bitmap = await createImageBitmap(file);
    const { width, height } = bitmap;
    const scale = Math.min(1, THUMB_MAX_EDGE / Math.max(width, height));
    const w = Math.max(1, Math.round(width * scale));
    const h = Math.max(1, Math.round(height * scale));
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      bitmap.close();
      return null;
    }
    ctx.drawImage(bitmap, 0, 0, w, h);
    bitmap.close();
    const blob = await new Promise<Blob | null>((resolve) => {
      canvas.toBlob((b) => resolve(b), 'image/jpeg', THUMB_QUALITY);
    });
    return blob;
  } catch {
    return null;
  }
}

/**
 * matched (rowIndex → File) を Storage に並列アップロード。
 * 返値: rowIndex → PhotoUploadInfo (アップロード成功分のみ)。
 */
export async function uploadLocalPhotos(
  districtId: string,
  rows: MappedRow[],
  matched: Map<number, File>,
  onProgress: (p: PhotoUploadProgress) => void,
): Promise<Map<number, PhotoUploadInfo>> {
  const rowById = new Map(rows.map((r) => [r.rowIndex, r]));
  const results = new Map<number, PhotoUploadInfo>();
  const entries = [...matched.entries()];
  const total = entries.length;
  let done = 0;
  const completedFiles: string[] = [];

  for (let i = 0; i < entries.length; i += CONCURRENCY) {
    const chunk = entries.slice(i, i + CONCURRENCY);
    await Promise.allSettled(
      chunk.map(async ([rowIndex, file]) => {
        const row = rowById.get(rowIndex);
        // postId は importExecutor が server-side で doc().id を発番するためここでは未確定。
        // storage.rules は `districts/{id}/posts/{postId}/{file=**}` のみ write 許可するため、
        // **posts/ 配下に独自 ID を発番** して upload する (rules を緩めない、CLAUDE.md / 要件遵守)。
        // photoUrl/photoThumbUrl は download URL として post doc に格納され、path の postId セグメントが
        // 実 doc id と一致しなくても表示は成立する (importExecutor は photoPath/photoUrl をそのまま保存)。
        const uploadId =
          typeof crypto !== 'undefined' && crypto.randomUUID
            ? crypto.randomUUID()
            : `import-${Date.now()}-${rowIndex}`;
        void row; // originalId は preview 統計用、path には使わない
        const basePath = `districts/${districtId}/posts/${uploadId}`;
        const originalPath = `${basePath}/original.jpg`;
        const thumbPath = `${basePath}/thumbnail.jpg`;
        try {
          const thumb = await generateThumbnail(file);
          await uploadBytes(ref(storage, originalPath), file, {
            contentType: file.type || 'image/jpeg',
          });
          await uploadBytes(ref(storage, thumbPath), thumb ?? file, {
            contentType: thumb ? 'image/jpeg' : file.type || 'image/jpeg',
          });
          const [photoUrl, photoThumbUrl] = await Promise.all([
            getDownloadURL(ref(storage, originalPath)).catch(() => null),
            getDownloadURL(ref(storage, thumbPath)).catch(() => null),
          ]);
          results.set(rowIndex, {
            photoPath: originalPath,
            photoUrl,
            photoThumbPath: thumbPath,
            photoThumbUrl,
          });
          completedFiles.push(file.name);
        } catch (e) {
          console.error(`Photo upload failed for row ${rowIndex}:`, e);
          completedFiles.push(`${file.name} (失敗)`);
        }
        done++;
        onProgress({
          current: done,
          total,
          currentFile: file.name,
          completedFiles: [...completedFiles],
        });
      }),
    );
  }

  return results;
}
